// ---------------------------------------------------------------------------
// Auth context — Amplify v6 (Cognito) integration
// ---------------------------------------------------------------------------
// Replaces the Phase-1 stub. Sign-in/out runs against the shared
// SharedUserPool configured at boot by main.tsx via amplify_outputs.json.
//
// Tokens are NOT persisted by us in localStorage — Amplify v6 handles
// that. Components read the AuthUser shape from the AuthContext; the
// api.ts client reaches for `fetchAuthSession()` directly each request
// so token rotation/refresh is invisible to callers.
//
// Role mapping (Cognito → CmsRole) reads the `custom:roleCode` claim — the
// consolidated role code from identity.system_users.role_code (security-plan
// P1). The legacy `custom:role` trio (amc_admin/read_write/read_only) was
// dropped at P1c and is no longer emitted, so reading it defaulted every user
// to TCM Staff. Survey module codes: 200 = TCM Admin, 201 = TCM Staff; a broad
// platform role (custom:allOrgNodes) is treated as TCM Admin too.
//
// `custom:orgNodeId` is not read (PLM-726): it was the user's lowest-numbered
// assigned org node, it granted nothing, and shared-services stops issuing it.
// Settings picks its customer from a list instead.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router";
import { Amplify } from "aws-amplify";
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  fetchAuthSession,
  type SignInOutput,
} from "aws-amplify/auth";
import type { CmsRole } from "@/types/workflow";

export interface AuthUser {
  userId: string;
  email: string;
  role: CmsRole;
}

type AuthMode = "loading" | "cognito" | "unconfigured";

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mode: AuthMode;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Cognito ID-token claim shape — the Pre-Token Generation V2 trigger
// (shared-services PR #190 / system-users.md §D10) is responsible for
// populating custom:* on every login.
interface CognitoIdClaims {
  sub: string;
  email?: string;
  "cognito:username"?: string;
  "custom:personId"?: string;
  "custom:roleCode"?: string;
  "custom:allOrgNodes"?: string;
  "custom:uiProfileCode"?: string;
}

// Survey-engine module role codes in the shared `roles` catalog.
const SURVEY_ADMIN_ROLE_CODE = "200"; // survey-admin → TCM Admin
const SURVEY_STAFF_ROLE_CODE = "201"; // survey-staff → TCM Staff
const SURVEY_REVIEWER_ROLE_CODE = "202"; // survey-reviewer → Health Plan (external)

function isCognitoConfigured(): boolean {
  try {
    const cfg = Amplify.getConfig();
    return Boolean(cfg.Auth?.Cognito?.userPoolId);
  } catch {
    return false;
  }
}

// Map the platform role code → survey CmsRole. Keyed off the explicit
// `custom:roleCode` claim (200/201/202 = survey admin/staff/reviewer), with a
// broad platform role (`custom:allOrgNodes === 'true'`, e.g. amc_admin) treated
// as TCM Admin. Anything unrecognized falls back to 'No Access' (grants
// nothing) — an UNMAPPED code is never Health Plan, which holds the privileged
// final-approval power; only the explicit reviewer code (202) maps there. The
// backend RBAC (approvalRoleFor) is the real gate regardless; this keeps the UI
// fail-closed too.
function mapToCmsRole(
  roleCode: string | undefined,
  allOrgNodes: string | undefined,
): CmsRole {
  if (roleCode === SURVEY_ADMIN_ROLE_CODE) return "TCM Admin";
  if (roleCode === SURVEY_STAFF_ROLE_CODE) return "TCM Staff";
  if (roleCode === SURVEY_REVIEWER_ROLE_CODE) return "Health Plan";
  if (allOrgNodes === "true") return "TCM Admin";
  return "No Access";
}

async function loadUserFromSession(): Promise<AuthUser | null> {
  try {
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload as
      | CognitoIdClaims
      | undefined;
    if (!payload) return null;
    return {
      userId: payload["custom:personId"] ?? payload.sub,
      email: payload.email ?? payload["cognito:username"] ?? "",
      // Dual-read `custom:roleCode` with a fallback to `custom:uiProfileCode`
      // for the P1a→P1c transition window (mirrors shared-runtime middleware).
      role: mapToCmsRole(
        payload["custom:roleCode"] ?? payload["custom:uiProfileCode"],
        payload["custom:allOrgNodes"],
      ),
    };
  } catch {
    return null;
  }
}

function describeSignInError(err: unknown): string {
  if (err instanceof Error) {
    switch (err.name) {
      case "NotAuthorizedException":
      case "UserNotFoundException":
        return "Incorrect email or password.";
      case "UserNotConfirmedException":
        return "Account not confirmed. Contact your administrator.";
      case "PasswordResetRequiredException":
        return "Password reset required. Contact your administrator.";
      case "TooManyRequestsException":
      case "LimitExceededException":
        return "Too many attempts. Please wait and try again.";
      default:
        return err.message;
    }
  }
  return "Sign-in failed. Please try again.";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    mode: "loading",
  });

  // Bootstrap — if Amplify already has a cached session from a prior
  // tab/window, surface it before the first render so guarded routes
  // don't bounce to /login.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isCognitoConfigured()) {
        if (cancelled) return;
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          mode: "unconfigured",
        });
        return;
      }
      const user = await loadUserFromSession();
      if (cancelled) return;
      setState({
        user,
        isAuthenticated: user !== null,
        isLoading: false,
        mode: "cognito",
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    if (!isCognitoConfigured()) {
      throw new Error(
        "Auth is not configured — amplify_outputs.json missing or invalid. Hard-refresh and try again, or contact an administrator.",
      );
    }

    let output: SignInOutput;
    try {
      output = await amplifySignIn({
        username: email,
        password,
        options: { authFlowType: "USER_PASSWORD_AUTH" },
      });
    } catch (err) {
      throw new Error(describeSignInError(err));
    }

    const step = output.nextStep.signInStep;
    if (step === "DONE") {
      const user = await loadUserFromSession();
      setState((s) => ({
        ...s,
        user,
        isAuthenticated: user !== null,
        isLoading: false,
      }));
      return;
    }

    // First-login password reset and MFA flows aren't surfaced as
    // dedicated screens yet — surface the challenge as an actionable
    // error so the user contacts an admin rather than getting stuck.
    if (step === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      throw new Error(
        "First-login password reset is required. Complete the password change in the AWS Console (Cognito User Pool → user → Reset password), then sign in again.",
      );
    }
    if (
      step === "CONFIRM_SIGN_IN_WITH_TOTP_CODE" ||
      step === "CONFIRM_SIGN_IN_WITH_SMS_CODE"
    ) {
      throw new Error(
        "MFA challenge is not yet supported in this UI. Disable MFA on your user, or use a non-MFA account.",
      );
    }
    throw new Error(`Unhandled sign-in challenge: ${step}`);
  }, []);

  const logout = useCallback(async () => {
    if (state.mode === "cognito") {
      try {
        await amplifySignOut();
      } catch {
        // Amplify throws when there's no active session — treat as a no-op.
      }
    }
    setState((s) => ({ ...s, user: null, isAuthenticated: false }));
  }, [state.mode]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

/** Route guard — redirects to /login if not authenticated. */
export function AuthGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
